import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  CheckCircle, Circle, Loader2, AlertTriangle,
  FileText, Upload, ChevronRight, ArrowRight,
  TrendingUp, Shield, Users, DollarSign
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MILESTONE_ICONS = {
  intake: FileText,
  underwriting: Shield,
  matching: Users,
  closing: DollarSign,
};

const STATUS_STYLES = {
  done: { icon: CheckCircle, color: "text-emerald-600", bg: "bg-emerald-600", ring: "ring-emerald-200", line: "bg-emerald-500" },
  in_progress: { icon: Loader2, color: "text-amber-600", bg: "bg-amber-500", ring: "ring-amber-200", line: "bg-amber-300" },
  pending: { icon: Circle, color: "text-[#ccc]", bg: "bg-[#ddd]", ring: "ring-[#EDE8DC]", line: "bg-[#EDE8DC]" },
};

export default function DealProgressTracker({ leadId, onOpenTab }) {
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!leadId) return;
    axios.get(`${API}/copilot/v2/deal-progress?lead_id=${leadId}`, { headers })
      .then(r => setProgress(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [leadId]);

  if (loading) return null;
  if (!progress) return null;

  const { milestones = [], nudges = [], completion_pct = 0, stage_label = "", risk_score, risk_band } = progress;

  return (
    <div className="rounded-2xl bg-white border border-[#EDE8DC] overflow-hidden shadow-[0_2px_20px_rgba(0,0,0,0.04)]" data-testid="deal-progress-tracker">
      {/* Header with progress ring */}
      <div className="p-5 bg-gradient-to-r from-emerald-50 to-white border-b border-emerald-100">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-bold text-[#0A0A0A] tracking-tight">Deal Progress</h3>
            <p className="text-[10px] text-[#888] mt-0.5">{stage_label} — {completion_pct}% complete</p>
          </div>
          <div className="relative h-12 w-12">
            <svg className="h-12 w-12 -rotate-90" viewBox="0 0 48 48">
              <circle cx="24" cy="24" r="20" fill="none" stroke="#f0ece4" strokeWidth="3" />
              <circle cx="24" cy="24" r="20" fill="none" stroke="#059669" strokeWidth="3"
                strokeDasharray={`${(completion_pct / 100) * 125.66} 125.66`}
                strokeLinecap="round"
                style={{ transition: "stroke-dashoffset 0.6s ease" }}
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-xs font-bold text-emerald-700">{completion_pct}%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Milestones */}
      <div className="p-5 space-y-0">
        {milestones.map((m, i) => {
          const style = STATUS_STYLES[m.status] || STATUS_STYLES.pending;
          const MilestoneIcon = MILESTONE_ICONS[m.key] || Circle;
          const StatusIcon = style.icon;
          const isLast = i === milestones.length - 1;
          return (
            <div key={m.key} className="flex gap-3" data-testid={`milestone-${m.key}`}>
              {/* Timeline line + dot */}
              <div className="flex flex-col items-center">
                <div className={`h-7 w-7 rounded-full flex items-center justify-center ring-2 ${style.ring} ${m.status === "done" ? "bg-emerald-600" : m.status === "in_progress" ? "bg-amber-50 border border-amber-300" : "bg-[#f8f6f1] border border-[#e0dcd4]"}`}>
                  {m.status === "done" ? (
                    <CheckCircle className="h-3.5 w-3.5 text-white" />
                  ) : m.status === "in_progress" ? (
                    <MilestoneIcon className="h-3.5 w-3.5 text-amber-600" />
                  ) : (
                    <MilestoneIcon className="h-3.5 w-3.5 text-[#ccc]" />
                  )}
                </div>
                {!isLast && (
                  <div className={`w-0.5 flex-1 my-1 rounded-full ${style.line}`} style={{ minHeight: "20px" }} />
                )}
              </div>
              {/* Content */}
              <div className={`pb-4 flex-1 ${isLast ? "" : ""}`}>
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-bold ${m.status === "done" ? "text-emerald-700" : m.status === "in_progress" ? "text-amber-700" : "text-[#999]"}`}>
                    {m.label}
                  </span>
                  {m.status === "done" && <span className="text-[8px] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-600 font-bold border border-emerald-200">Done</span>}
                  {m.status === "in_progress" && <span className="text-[8px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-600 font-bold border border-amber-200">In Progress</span>}
                </div>
                <p className="text-[10px] text-[#888] mt-0.5">{m.detail}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Smart Nudges */}
      {nudges.length > 0 && (
        <div className="px-5 pb-5 space-y-2" data-testid="deal-progress-nudges">
          <div className="text-[9px] uppercase tracking-[0.15em] text-[#888] font-bold mb-1">What to do next</div>
          {nudges.map((nudge, i) => {
            const isAction = nudge.action_type && onOpenTab;
            const tabMap = { complete_fields: "edit", upload_doc: "documents_full", resolve_blocker: "exceptions" };
            return (
              <button
                key={i}
                onClick={() => isAction && tabMap[nudge.action_type] && onOpenTab(tabMap[nudge.action_type])}
                disabled={!isAction || !tabMap[nudge.action_type]}
                className={`flex items-center gap-2.5 p-2.5 rounded-lg w-full text-left transition-all ${
                  nudge.priority === "critical" ? "bg-red-50 border border-red-200 hover:shadow-md" :
                  nudge.priority === "high" ? "bg-amber-50 border border-amber-200 hover:shadow-md" :
                  "bg-[#FDFCF9] border border-[#EDE8DC]"
                } ${isAction && tabMap[nudge.action_type] ? "cursor-pointer hover:scale-[1.01]" : ""}`}
                data-testid={`nudge-${i}`}
              >
                {nudge.priority === "critical" ? (
                  <AlertTriangle className="h-3.5 w-3.5 text-red-500 shrink-0" />
                ) : nudge.type === "action" ? (
                  <ArrowRight className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                ) : (
                  <TrendingUp className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                )}
                <span className={`text-xs font-medium ${
                  nudge.priority === "critical" ? "text-red-700" :
                  nudge.priority === "high" ? "text-amber-700" : "text-[#555]"
                }`}>{nudge.message}</span>
                {isAction && tabMap[nudge.action_type] && <ChevronRight className="h-3 w-3 text-[#bbb] ml-auto shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
